<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import PlsButton1 from '@/components/PlsButton1.vue';
import LoadingIcon from '@/components/icons/LoadingIcon.vue';
import { useI18n } from '@/plugins/i18n';
import { analytics } from '@/common/logs';
import { GENERAL_EVENTS, BTN_EVTS, CRITICAL_EVENTS } from '@/common/logs/events';
import { useOTPAllCustomisation } from '../../composables/guest-form/useOTPCustomisation';
import { useGuestFormPrimaryActionButton } from '../../composables/guest-form/useGuestFormActionsCustomisation';
import {
  combineStyles,
  createBackgroundStyle,
  createBorderStyle,
  createTextStyle,
  resolveButtonStateColor,
} from '@/common/customisation/utils/styleUtils';

export interface OtpGuestData {
  email: string;
  name: string;
  phone: string;
  description: string;
}

interface Props {
  guestData: OtpGuestData;
  onVerifySuccess: (otpCode: string) => Promise<void>;
  onResendOtp: () => Promise<void>;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'change-email'): void;
  (e: 'support-request:cancel', payload: Record<string, unknown>): void;
}>();

const { t } = useI18n();

const otpCustomisation = useOTPAllCustomisation();
const {
  title,
  errorBanner,
  text,
  input,
  error,
  resendButton,
  resendingText,
  resendTimer,
  changeEmailButton,
  // cancelButton: cancelButtonCustomisation,
} = otpCustomisation;

const primaryAction = useGuestFormPrimaryActionButton();

const OTP_LENGTH = 6;
const resendTimeouts = [30, 60, 120] as const;

const loading = ref(false);
const isResending = ref(false);
const otpCode = ref('');
const otpError = ref(false);
const otpErrorMessage = ref('');
const resendAttempts = ref(0);
const maxResendAttempts = 3;
const remainingSeconds = ref(0);
const resendTimerId = ref<ReturnType<typeof setInterval> | null>(null);

const errInfo = ref({
  isErr: false,
  message: '',
});

const interpolate = (template: string, vars: Record<string, string>): string => {
  let result = template;
  for (const [key, value] of Object.entries(vars)) {
    result = result.split(`{${key}}`).join(value);
  }
  return result;
};

const canResend = computed(
  () => resendAttempts.value < maxResendAttempts && remainingSeconds.value === 0 && !isResending.value
);

const titleDisplay = computed(() => {
  const custom = title.titleText.value?.trim();
  return custom || (t('page.otp.verification.title') as string);
});

const titleStyle = computed(() => createTextStyle(title.titleTextColor));

const otpErrorBannerStyle = computed(() =>
  combineStyles(
    createBackgroundStyle(errorBanner.backgroundColor),
    createBorderStyle(errorBanner.borderColor),
    createTextStyle(errorBanner.textColor)
  )
);

const otpErrorBannerDismissStyle = computed(() => createTextStyle(errorBanner.dismissActionColor));

const instructionText = computed(() => {
  const custom = text.text.value?.trim();
  if (custom) {
    return interpolate(custom, { email: props.guestData.email });
  }
  return t('page.otp.verification.description', { email: props.guestData.email }) as string;
});

const instructionStyle = computed(() => createTextStyle(text.textColor));

const otpPlaceholder = computed(() => {
  const custom = input.placeholder.value?.trim();
  return custom || (t('page.otp.verification.placeholder') as string);
});

const otpInputStyle = computed(() => {
  const hasErr = otpError.value;
  const border = hasErr ? (input.errorBorderColor.value ?? input.borderColor.value) : input.borderColor.value;
  const ph = input.placeholderColor.value;
  return {
    color: input.textColor.value ?? undefined,
    backgroundColor: input.backgroundColor.value ?? undefined,
    border: border ? `2px solid ${border}` : undefined,
    ...(ph ? { '--widget-guest-otp-ph': ph } : {}),
  } as Record<string, string>;
});

const fieldErrorStyle = computed(() => createTextStyle(error.errorTextColor));

const resendLabel = computed(() => {
  const custom = resendButton.buttonText.value?.trim();
  return custom || (t('page.otp.verification.resend') as string);
});

const resendControlStyle = computed(() => {
  if (isResending.value) {
    return combineStyles(
      createBackgroundStyle(resendButton.disabledButtonBackgroundColor),
      createBorderStyle(resendButton.disabledButtonBorderColor),
      createTextStyle(resendingText.textColor)
    );
  }
  return combineStyles(
    createBackgroundStyle(resendButton.buttonBackgroundColor),
    createBorderStyle(resendButton.buttonBorderColor),
    createTextStyle(resendButton.buttonTextColor)
  );
});

const resendingLabel = computed(() => {
  const custom = resendingText.text.value?.trim();
  return custom || (t('page.otp.verification.resending') as string);
});

const timerDisplay = computed(() => {
  const custom = resendTimer.timerText.value?.trim();
  if (custom) {
    return interpolate(custom, { seconds: String(remainingSeconds.value) });
  }
  return t('page.otp.verification.resend_in', { seconds: remainingSeconds.value }) as string;
});

const timerStyle = computed(() => createTextStyle(resendTimer.timerTextColor));

const changeEmailLabel = computed(() => {
  const custom = changeEmailButton.buttonText.value?.trim();
  return custom || (t('page.otp.verification.change_email') as string);
});

const changeEmailButtonStyle = computed(() => ({
  ...createTextStyle(changeEmailButton.buttonTextColor),
  border:
    changeEmailButton.buttonBorderColor.value && `${changeEmailButton.buttonBorderColor.value}`.trim().length > 0
      ? `1px solid ${changeEmailButton.buttonBorderColor.value}`
      : undefined,
  backgroundColor: changeEmailButton.buttonBackgroundColor.value ?? undefined,
}));

const verifyButtonText = computed(() => {
  const custom = primaryAction.buttonText.value?.trim();
  return custom || (t('page.otp.verification.verify') as string);
});

const isVerifyDisabled = computed(() => otpCode.value.length !== OTP_LENGTH || loading.value);

const verifyButtonBackgroundColor = computed(() =>
  resolveButtonStateColor(
    isVerifyDisabled.value,
    primaryAction.disabledButtonBackgroundColor.value,
    primaryAction.buttonBackgroundColor.value
  )
);

const verifyButtonBorderColor = computed(() =>
  resolveButtonStateColor(
    isVerifyDisabled.value,
    primaryAction.disabledButtonBorderColor.value,
    primaryAction.buttonBorderColor.value
  )
);

const verifyButtonTextColor = computed(() =>
  resolveButtonStateColor(
    isVerifyDisabled.value,
    primaryAction.disabledButtonTextColor.value,
    primaryAction.buttonTextColor.value
  )
);

// const cancelLabel = computed(() => {
//   const custom = cancelButtonCustomisation.buttonText.value?.trim();
//   return custom || (t('global.cancel', 'Cancel') as string);
// });

// const cancelNavStyle = computed(() =>
//   combineStyles(
//     createBackgroundStyle(cancelButtonCustomisation.buttonBackgroundColor),
//     createBorderStyle(cancelButtonCustomisation.buttonBorderColor),
//     createTextStyle(cancelButtonCustomisation.buttonTextColor)
//   )
// );

const handleOtpInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  let value = target.value.replace(/\D/g, '');
  if (value.length > OTP_LENGTH) {
    value = value.substring(0, OTP_LENGTH);
  }
  otpCode.value = value;
  otpError.value = false;
};

const handleOtpFocus = () => {
  otpError.value = false;
};

const handleVerificationError = (verificationError: unknown) => {
  const errorMessage =
    verificationError && typeof verificationError === 'object' && 'message' in verificationError
      ? String((verificationError as { message?: string }).message)
      : '';
  const apiMessage =
    verificationError &&
    typeof verificationError === 'object' &&
    'response' in verificationError &&
    (verificationError as { response?: { data?: { message?: string } } }).response?.data?.message
      ? String((verificationError as { response: { data: { message?: string } } }).response.data.message)
      : '';

  const combined = errorMessage || apiMessage || '';

  if (combined.includes('Invalid')) {
    otpError.value = true;
    const customErr = error.errorText.value?.trim();
    otpErrorMessage.value = customErr || (t('page.otp.verification.incorrect') as string);
    analytics.track({
      name: GENERAL_EVENTS.GUEST_OTP_INCORRECT,
      metadata: {
        email: props.guestData.email,
        error: combined,
      },
    });
    return;
  }

  errInfo.value = {
    isErr: true,
    message: t('page.otp.verification.general_error') as string,
  };
  analytics.trackCriticalEvent({
    name: CRITICAL_EVENTS.GUEST_OTP_GENERAL_ERROR,
    metadata: {
      email: props.guestData.email,
      error: combined || 'Unknown error',
    },
  });
};

const handleVerify = async () => {
  if (otpCode.value.length !== OTP_LENGTH) {
    otpError.value = true;
    otpErrorMessage.value = t('page.otp.verification.invalid_length') as string;
    return;
  }

  loading.value = true;
  otpError.value = false;

  try {
    await props.onVerifySuccess(otpCode.value);
  } catch (verifyError: unknown) {
    handleVerificationError(verifyError);
  } finally {
    loading.value = false;
  }
};

const handleResendOtp = async () => {
  if (!canResend.value || isResending.value) {
    return;
  }

  isResending.value = true;
  resendAttempts.value += 1;

  analytics.track({
    name: GENERAL_EVENTS.GUEST_OTP_RESEND_ATTEMPT,
    metadata: {
      email: props.guestData.email,
      attempt: resendAttempts.value,
    },
  });

  try {
    await props.onResendOtp();
    startResendTimer();
    analytics.track({
      name: GENERAL_EVENTS.GUEST_OTP_RESEND_SUCCESS,
      metadata: {
        email: props.guestData.email,
      },
    });
  } catch (resendError: unknown) {
    const message = resendError instanceof Error ? resendError.message : '';
    errInfo.value = {
      isErr: true,
      message: t('page.otp.verification.resend_error') as string,
    };
    analytics.trackCriticalEvent({
      name: GENERAL_EVENTS.GUEST_OTP_RESEND_FAILED,
      metadata: {
        email: props.guestData.email,
        attempt: resendAttempts.value,
        error: message || 'Unknown error',
      },
    });
  } finally {
    isResending.value = false;
  }
};

const handleChangeEmail = () => {
  analytics.trackClick({
    name: BTN_EVTS.GUEST_OTP_CHANGE_EMAIL,
    metadata: {
      email: props.guestData.email,
    },
  });
  emit('change-email');
};

const handleCancel = () => {
  emit('support-request:cancel', {});
};

const handleCancelKeyDown = (event: KeyboardEvent) => {
  if (event.key !== 'Enter' && event.key !== ' ') {
    return;
  }
  event.preventDefault();
  handleCancel();
};

const handleClearErr = () => {
  errInfo.value = { isErr: false, message: '' };
};

const handleClearErrKeyDown = (event: KeyboardEvent) => {
  if (event.key !== 'Enter' && event.key !== ' ') {
    return;
  }
  event.preventDefault();
  handleClearErr();
};

const clearResendTimer = () => {
  if (resendTimerId.value) {
    clearInterval(resendTimerId.value);
    resendTimerId.value = null;
  }
  remainingSeconds.value = 0;
};

const startResendTimer = () => {
  clearResendTimer();
  const timeoutIndex = Math.min(resendAttempts.value, resendTimeouts.length - 1);
  remainingSeconds.value = resendTimeouts[timeoutIndex];

  resendTimerId.value = setInterval(() => {
    remainingSeconds.value -= 1;
    if (remainingSeconds.value <= 0) {
      if (resendTimerId.value) {
        clearInterval(resendTimerId.value);
        resendTimerId.value = null;
      }
      remainingSeconds.value = 0;
    }
  }, 1000);
};

onMounted(() => {
  startResendTimer();
});

onBeforeUnmount(() => {
  clearResendTimer();
});
</script>

<template>
  <div class="shadow-hard theme-font-main widget-guest-otp" role="region" :aria-label="titleDisplay">
    <div class="widget-guest-otp__inner">
      <div class="widget-guest-otp__title-row">
        <div class="theme-font-h4 widget-guest-otp__title" :style="titleStyle">
          {{ titleDisplay }}
        </div>
        <div
          v-if="errInfo.isErr"
          class="theme-font-subtext widget-guest-otp__banner"
          role="alert"
          :style="otpErrorBannerStyle"
        >
          <div class="widget-guest-otp__banner-icon" aria-hidden="true">
            <img :src="require('@/assets/img/error.png')" alt="" />
          </div>
          <div class="widget-guest-otp__banner-text">{{ errInfo.message }}</div>
          <button
            type="button"
            class="theme-font-h4 widget-guest-otp__banner-dismiss"
            :style="otpErrorBannerDismissStyle"
            :aria-label="t('global.close', 'Close')"
            tabindex="0"
            @click="handleClearErr"
            @keydown="handleClearErrKeyDown"
          >
            ×
          </button>
        </div>
      </div>

      <p class="theme-font-main widget-guest-otp__description" :style="instructionStyle">
        {{ instructionText }}
      </p>

      <div class="widget-guest-otp__field">
        <input
          :value="otpCode"
          type="text"
          inputmode="numeric"
          autocomplete="one-time-code"
          :maxlength="OTP_LENGTH"
          :placeholder="otpPlaceholder"
          class="input-radius theme-font-h4 widget-guest-otp__input"
          :style="otpInputStyle"
          :aria-invalid="otpError"
          :aria-describedby="otpError ? 'widget-guest-otp-err' : undefined"
          @input="handleOtpInput"
          @focus="handleOtpFocus"
        />
        <p
          v-if="otpError"
          id="widget-guest-otp-err"
          class="theme-font-subtext widget-guest-otp__field-error"
          :style="fieldErrorStyle"
        >
          <span aria-hidden="true">*</span>
          {{ otpErrorMessage }}
        </p>
      </div>

      <div class="widget-guest-otp__resend">
        <button
          v-if="canResend || isResending"
          type="button"
          class="theme-font-main widget-guest-otp__link"
          :style="resendControlStyle"
          :disabled="!canResend || isResending"
          @click="handleResendOtp"
        >
          {{ isResending ? resendingLabel : resendLabel }}
        </button>
        <div v-else class="theme-font-subtext widget-guest-otp__timer" :style="timerStyle">
          {{ timerDisplay }}
        </div>
      </div>

      <div class="widget-guest-otp__actions">
        <!-- <button
          type="button"
          class="theme-font-main widget-guest-otp__cancel-btn"
          :style="cancelNavStyle"
          :aria-label="cancelLabel"
          tabindex="0"
          @click="handleCancel"
          @keydown="handleCancelKeyDown"
        >
          {{ cancelLabel }}
        </button> -->
        <PlsButton1
          :aria-label="verifyButtonText"
          :aria-busy="loading"
          :border-color="verifyButtonBorderColor"
          :background-color="verifyButtonBackgroundColor"
          :text-color="verifyButtonTextColor"
          :disabled="isVerifyDisabled"
          class="theme-font-main widget-guest-otp__submit widget-guest-otp__submit--paired"
          @click="handleVerify"
        >
          <span class="widget-guest-otp__submit-content">
            <LoadingIcon v-if="loading" class="widget-guest-otp__submit-spinner" aria-hidden="true" />
            <span>{{ verifyButtonText }}</span>
          </span>
        </PlsButton1>
      </div>

      <div class="widget-guest-otp__secondary">
        <button
          type="button"
          class="theme-font-main widget-guest-otp__change-email"
          :style="changeEmailButtonStyle"
          :aria-label="changeEmailLabel"
          tabindex="0"
          @click="handleChangeEmail"
        >
          {{ changeEmailLabel }}
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.widget-guest-otp {
  width: 100%;
  box-sizing: border-box;
  padding: 12px 16px 16px;
}

.widget-guest-otp__inner {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.widget-guest-otp__title-row {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
}

.widget-guest-otp__banner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  max-width: 22.3rem;
}

.widget-guest-otp__banner-icon {
  height: 1.5rem;
  flex-shrink: 0;

  img {
    width: auto;
    height: 100%;
    display: block;
  }
}

.widget-guest-otp__banner-text {
  flex: 1;
  text-align: left;
}

.widget-guest-otp__banner-dismiss {
  margin: 0;
  padding: 0 4px;
  border: none;
  cursor: pointer;
  flex-shrink: 0;
}

.widget-guest-otp__description {
  margin: 0;
  text-align: center;
}

.widget-guest-otp__field {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
}

.widget-guest-otp__input {
  width: 100%;
  box-sizing: border-box;
  padding: 12px;
  text-align: center;
  letter-spacing: 0.2rem;
  outline: none;

  &::placeholder {
    color: var(--widget-guest-otp-ph);
    opacity: 1;
  }
}

.widget-guest-otp__field-error {
  margin: 0;
  text-align: center;
}

.widget-guest-otp__resend {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 2.5rem;
}

.widget-guest-otp__link {
  margin: 0;
  padding: 8px;
  border: none;
  cursor: pointer;
  text-decoration: underline;
}

.widget-guest-otp__timer {
  text-align: center;
}

.widget-guest-otp__actions {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  gap: 12px;
  width: 100%;
  box-sizing: border-box;
  margin-top: 4px;
}

.widget-guest-otp__cancel-btn {
  flex: 1 1 0;
  min-width: 0;
  margin: 0;
  padding: 12px 16px;
  border-radius: 8px;
  cursor: pointer;
  box-sizing: border-box;
  text-align: center;
  text-decoration: none;
}

.widget-guest-otp__submit.widget-guest-otp__submit--paired {
  flex: 1 1 0;
  min-width: 0;
  max-width: none;
  width: auto;
  padding: 12px 16px;
}

.widget-guest-otp__submit-content {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.widget-guest-otp__submit-spinner {
  flex-shrink: 0;
}

.widget-guest-otp__secondary {
  display: flex;
  justify-content: center;
}

.widget-guest-otp__change-email {
  margin: 0;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  text-decoration: underline;
  text-align: center;
}
</style>
